import {z} from 'zod';
import dedent from 'dedent';
import {
  ISO_COUNTRIES,
  ISO_LANGUAGES,
} from '@packages/systems/localization-constants';
import {
  normalizeLanguageTagString,
  parseLanguageTag,
} from '@packages/domains/localization/translation/data-types';

/**
 * Common schemas for Webflow Designer API tools
 */

export const breakpointSchema = z
  .enum(['xxl', 'xl', 'large', 'main', 'medium', 'small', 'tiny'])
  .optional()
  .describe('The responsive breakpoint to target.');

export const pseudoSchema = z
  .enum([
    'noPseudo',
    'nth-child(odd)',
    'nth-child(even)',
    'first-child',
    'last-child',
    'hover',
    'active',
    'visited',
    'focus',
    'focus-visible',
    'focus-within',
    'placeholder',
    'empty',
    'before',
    'after',
  ])
  .optional()
  .describe('The CSS pseudo-class to target.');

export const styleIdSchema = z.string().describe('The ID of the style.');

export const styleNameSchema = z.string().describe('The name of the style.');

export const cssPropertySchema = z
  .string()
  .describe(
    'The name of a CSS property (e.g., "background-color", "font-size", "box-shadow").'
  );

export const cssPropertyValueSchema = z
  .string()
  .describe(
    'The value for a CSS property. Can be a string value or a variable reference.'
  );

export const cssPropertyArraySchema = z
  .array(z.string())
  .describe('An array of CSS property names.');

export const cssPropertyMapSchema = z
  .record(z.string())
  .describe('A PropertyMap object containing CSS properties and their values.');

export const elementIdSchema = z.string().describe(dedent`
    The element ID — the exact value of a \`data-w-id\` attribute copied verbatim from a prior read tool result (read-page, get-html-from-selected-element, get-html-from-query-selectors, get-html-from-text).
    When targeting an existing element, use only a \`data-w-id\` value from a prior read result. Do not guess or fabricate IDs.
    Never use values from \`data-w-instance-of\`, \`data-w-component-id\`, \`w-node-*\` ids/classes, \`data-props-*\` payloads, or \`data-w-collection-*\` attributes as element IDs.`);

export const elementPresetSchema = z
  .string()
  .describe(
    'An element preset derived from the webflow.elementPresets object.'
  );

/**
 * Common breakpoint and pseudo options object
 */
export const breakpointAndPseudoSchema = z.object({
  breakpoint: breakpointSchema,
  pseudo: pseudoSchema,
});

export type BreakpointId = z.infer<typeof breakpointSchema>;
export type PseudoStateKey = z.infer<typeof pseudoSchema>;

/**
 * Variable-related schemas
 */
export const variableCollectionIdSchema = z
  .string()
  .describe('The ID of the variable collection.');

export const variableNameSchema = z
  .string()
  .describe('The name of the variable.');

export const variableIdSchema = z.string().describe('The ID of the variable.');

export const customVariableValueSchema = z
  .object({
    type: z.literal('custom'),
    value: z.string(),
  })
  .describe(
    'A custom value object with type "custom" and a string value for advanced CSS functions like clamp(), calc(), min(), max(), var(), etc.'
  );

export const sizeValueSchema = z
  .object({
    unit: z.enum([
      'px',
      'rem',
      'em',
      'vw',
      'vh',
      'svh',
      'svw',
      'ch',
      'vmin',
      'vmax',
      'dvh',
      'dvw',
      'lvh',
      'lvw',
    ]),
    value: z.number(),
  })
  .describe(
    'A size value object with unit and numeric value for size variables'
  );

export const cssSchema = z.string().optional().describe(dedent`
  # CSS Styles

  The class name to apply styles to.

  ## Selectors

  **ONLY single class selectors or combo classes.** No nested, descendant, or element selectors.

  ✅ .device-image, .card-title, .button.cta
  ❌ .device-frame img, .parent .child, img, div, button

  If you need to style a child element, add a unique class to it directly:
  ❌ .card img → ✅ .card-image (applied via class="" on the <img>)

  ## Hover Effects on Child Elements
  To change a child element on parent hover, you CANNOT use nested selectors.
  Apply hover directly to the child class instead:
  ❌ .card:hover .card-image { opacity: 1; }
  ✅ .card-image:hover { opacity: 1; }

  ## Pseudo-elements
  ::before and ::after are NOT supported. Add a real HTML element with a class instead:
  ❌ .section::before { content: ""; position: absolute; background-color: black; }
  ✅ Add <div class="section-overlay"></div> as a child in the HTML, then style .section-overlay

  ## Universal Selector
  The universal selector (*) is not supported.
  ❌ .container > * { padding: 2px; }
  ✅ Add specific classes to child elements (if needed) instead:
     .container-item { color: red; }

  ## Pseudo Selectors
  Allowed pseudo-classes: :hover, :focus, and :active (trailing on a class selector).
  ❌ Other pseudo-classes (:nth-child, :visited, :focus-visible, etc.) and pseudo-elements (::before, ::after — see above).

  ## Properties
  ALWAYS use longhand properties, never shorthand.
  ✅ margin-top, padding-right, border-top-width, background-color, font-size, transition-property
  ❌ margin, padding, border, background, font, transition

  ## Animations
  No @keyframes or animation properties. Use transition only.

  ## Forbidden
  Never use \`!important\`. It is not allowed and will cause an error. Manage specificity with class selectors instead.

  ## Values
  - Always include units: ✅ 16px, 2rem, 50% ❌ 16, large
  - Colors: use full hex (#ffffff not #fff), rgb(), rgba(), hsl(), hsla(), or named colors
  - rgb/rgba: use comma syntax. ✅ rgba(0, 0, 0, 0.5) ❌ rgb(0 0 0)
  - Gradients: use background-image, not background shorthand
    ✅ background-image: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    ✅ background-image: radial-gradient(circle, #ffffff 0%, #e0e0e0 100%);
    ❌ background: linear-gradient(...)
  - font-family: single font name in quotes, no fallbacks.
    ✅ font-family: 'Inter';  ❌ font-family: 'Roboto', sans-serif;
  - Reset: use \`unset\` to remove a property (not 0px)

  ## Breakpoints
  - **main**: Desktop (no media query)
  - **large**: max-width: 991px
  - **medium**: max-width: 767px
  - **small**: max-width: 479px

  Example:
  \`\`\`css
  .hero-section { padding-top: 80px; padding-bottom: 80px; }
  @media screen and (max-width: 991px) { .hero-section { padding-top: 60px; padding-bottom: 60px; } }
  @media screen and (max-width: 479px) { .hero-section { padding-top: 30px; padding-bottom: 30px; } }
  \`\`\`

  ## CSS Grid
  No repeat() or minmax(). Use fixed values:
  ❌ grid-template-columns: repeat(3, 1fr)
  ✅ grid-template-columns: 1fr 1fr 1fr

  ## Typography
  When using headings or paragraphs, always set margin-top, margin-bottom, padding-top, padding-bottom, and line-height explicitly. Do not rely on browser defaults.

  ## Variables in CSS
  Reference variables using var() with the variable name format \`--_<collection>---<name>\`:
  \`\`\`css
  .hero-section { color: var(--_colors---primary); font-size: var(--_typography---heading-size); }
  \`\`\`

  ## Webflow w-* Classes
  Classes prefixed with w-* have opinionated default styles and cannot be directly edited.
  To override, create a combo class using render and style that combo class:
  ✅ .w-dropdown-toggle.toggle-control { background-color: red; }
  ❌ .w-dropdown-toggle { background-color: red; }

  ## Mode Applications
  Apply variable collection modes using the \`---mode--\` property syntax:
  \`---mode--{collectionName}: {modeName};\`

  ✅ ---mode--colors: light;
  ✅ ---mode--typography: dark;
  ❌ ---mode-colors: light; (wrong prefix, use two dashes)

  Add within CSS rule blocks alongside regular properties:
  \`\`\`css
  .section { color: red; ---mode--colors: light; padding-top: 16px; }
  \`\`\`

  Different modes per selector:
  \`\`\`css
  .section { ---mode--colors: light; }
  .other-section { ---mode--colors: dark; }
  \`\`\`

  Multiple modes from different collections in one rule:
  \`\`\`css
  .section { ---mode--colors: light; ---mode--typography: large; }
  \`\`\`

  Modes work in media queries and with combo classes.
  Collection and mode names are case-insensitive. If a collection or mode doesn't exist, the tool returns an error listing available options.
`);

export const variableCssSchema = z.string().optional().describe(dedent`
  # Variable CSS

  HTML-style CSS declaring variables with collection and mode associations.

  ## Format
  \`\`\`css
  html[data-collection="colors"][data-mode="base"] {
    --_colors---button-bg: #1971ff;
  }
  html[data-collection="colors"][data-mode="dark"] {
    --_colors---button-bg: #0a58ca;
  }
  \`\`\`

  ## Variable Name Format
  \`--_<collection>---<name>\` or \`--_<collection>---<group>--<name>\`
  - \`--_colors---primary\` → collection="colors", name="primary"
  - \`--_colors---button--bg\` → collection="colors", group="button", name="bg"

  ## Attributes
  - \`data-collection\`: collection name (e.g. "colors", "typography")
  - \`data-mode\`: mode name (e.g. "base", "dark", "light")

  ## Variable References
  Variables can reference other variables:
  \`--_colors---button-bg: var(--_colors---primary);\`

  ## Supported Value Types

  **Color**: hex (#000, #ff0000), rgb/rgba, hsl/hsla, named colors.
  No gradients (linear-gradient, radial-gradient), no url().

  **Size**: numeric + unit. Supported: px, em, rem, vh, vw, svh, svw, ch.
  Not supported: pt, or values without units.

  **Percentage**: numeric + %. e.g. 100%, 50%, 0.5%.

  **Number**: numeric without units. e.g. 0, 1.5, -5.

  **Font Family**: single font name only (e.g. "Arial", "Open Sans").
  No font stacks ("Arial, Helvetica, sans-serif").

  ## Base Mode
  Every collection has exactly one "base" mode. The base mode must have a value.
  Set data-mode="base" to write to it. Other modes override the base.
`);

// Reuses the same ISO tables LanguageTargetInternalService validates
// against, so a tag considered valid here is valid everywhere else in the
// system.
const isKnownLanguageTag = (tag: string): boolean => {
  const {language, region} = parseLanguageTag(tag);
  if (!Object.hasOwn(ISO_LANGUAGES, language)) return false;
  if (region !== undefined && !Object.hasOwn(ISO_COUNTRIES, region)) {
    return false;
  }
  return true;
};

export const languageTagZod = z
  .string()
  .min(1)
  .transform(normalizeLanguageTagString)
  .refine(isKnownLanguageTag, (tag) => ({
    message: `'${tag}' is not a valid BCP-47 language tag (e.g. 'es', 'pt-BR') -- get valid tags via the list-target-languages tool`,
  }));

// Shared by bulk-update-translation-exclusions and
// update-all-matching-translation-exclusions -- keep the two manifests in sync.
export const translationExclusionUpdateZod = z
  .object({
    languages: z
      .array(languageTagZod)
      .optional()
      .describe(
        "BCP-47 language tags this rule applies to (e.g. 'es', 'pt-BR') -- get valid tags via the list-target-languages tool first. Replaces the existing list; omit to leave unchanged. An empty array means all languages."
      ),
    triggers: z
      .array(z.enum(['auto', 'manual']))
      .optional()
      .describe(
        'Which translation triggers this rule applies to. Replaces the existing list; omit to leave unchanged.'
      ),
  })
  .refine(
    (value) => value.languages !== undefined || value.triggers !== undefined,
    {
      message: 'update must include languages and/or triggers',
    }
  );
